<script lang="ts">
    import { getContext, onDestroy, tick } from 'svelte'
    import { SvelteSet } from 'svelte/reactivity'

    import type { GenOrder, IOrder, TOrderStatus } from '../order'

    import { gettext as _, resolve, tgettext } from 'hydria/services/gettext'
    import type { TGettext } from 'hydria/services/gettext'
    import { simpleFetch } from 'hydria/services/session'
    import type { SimpleFetchResult } from 'hydria/services/session'

    import LoadingPattern from 'hydria/components/LoadingPattern.svelte'
    import ObPayBillLater from './Payments/BillLater.svelte'
    import ObPayCheque from './Payments/Cheque.svelte'
    import ObPayCreditCard from './Payments/CreditCard.svelte'
    import ObPayInternal from './Payments/Internal.svelte'
    import ObPayQuotation from './Payments/Quotation.svelte'
    import ObPayWire from './Payments/Wire.svelte'

    const unsubs: any[] = []
    onDestroy(() => {
        for (const unsub of unsubs) unsub.unsubscribe != null ? unsub.unsubscribe() : unsub()
    })

    // const orderId = getContext('orderId') as Readable<string>
    const Order = getContext('Order') as GenOrder

    let status = $state('loading' as 'not-found' | 'ready' | 'loading' | 'error')
    unsubs.push(
        Order.status.subscribe((v) => {
            status = v
        }),
    )

    let order: IOrder = $state(undefined as unknown as IOrder)
    let orderStatus: TOrderStatus | undefined = $state(undefined)
    unsubs.push(
        Order.orderStore.subscribe(($order) => {
            order = $order
            orderStatus = order.status
            tick().then(() => {
                if (order.owner && loadedUserId != order.owner) loadAllowedMethods(order.owner)
            })
        }),
    )

    type Method = 'bill later' | 'quotation' | 'credit card' | 'cheque' | 'wire' | 'internal' | 'q-bill later'
    interface Tab {
        id: Method
        text: TGettext
        component: any
    }
    const tabs: Tab[] = [
        {
            id: 'bill later',
            text: tgettext('Bill later'),
            component: ObPayBillLater,
        },
        {
            id: 'quotation',
            text: tgettext('Quotation'),
            component: ObPayQuotation,
        },
        {
            id: 'credit card',
            text: tgettext('Credit card'),
            component: ObPayCreditCard,
        },
        {
            id: 'cheque',
            text: tgettext('Cheque'),
            component: ObPayCheque,
        },
        {
            id: 'wire',
            text: tgettext('Wire transfer'),
            component: ObPayWire,
        },
        {
            id: 'internal',
            text: tgettext('Internal'),
            component: ObPayInternal,
        },
        {
            id: 'q-bill later',
            text: tgettext('Quotation'),
            component: ObPayBillLater,
        },
    ]
    let current: Tab | undefined = $state()

    let activeTabs: Tab[] = $derived(tabs.filter((tab) => allowedPaymentMethods.has(tab.id)))

    let userAllowedPaymentMethods: SvelteSet<Method> = $state(new SvelteSet())
    // Same as userAllowed, except quotation|billLater that are only allowed when draft/ready
    let allowedPaymentMethods: SvelteSet<Method> = $derived(status == 'ready' ? genAllowedMethods(userAllowedPaymentMethods, orderStatus) : new SvelteSet())

    function genAllowedMethods(userAllowedPaymentMethods: Set<Method>, orderStatus: TOrderStatus | undefined): SvelteSet<Method> {
        let allowedPaymentMethods: SvelteSet<Method>
        if (orderStatus == 'quotation') {
            allowedPaymentMethods = new SvelteSet(['q-bill later'])
        } else {
            allowedPaymentMethods = new SvelteSet([...userAllowedPaymentMethods].filter((m) => (['quotation', 'bill later'].includes(m) ? ['draft', 'ready'].includes(orderStatus ? orderStatus : '') : true)))
        }

        return allowedPaymentMethods
    }

    let loadedUserId: string
    let loadingMethods = $state(false)
    async function loadAllowedMethods(ownerId: string) {
        if (ownerId == loadedUserId) return

        loadingMethods = true
        let r: SimpleFetchResult
        try {
            r = await simpleFetch('GET', `/json/ecom/1/user-payment-types/${order.owner}`)
        } catch (e: any) {
            r = e
            console.error('Failed to load allowed methods:', e)
        }

        if (r && r.data) {
            // r.data.types: quotation, bill later
            userAllowedPaymentMethods = new SvelteSet([...r.data.types, 'credit card', 'cheque', 'wire', 'internal'])
            // current = tabs.filter((t) => t.id === 'credit card')[0]
        }

        loadingMethods = false
        loadedUserId = ownerId
    }

    function setInitialTab() {
        if (status == 'ready' && activeTabs.length && (!current || !activeTabs.includes(current))) {
            let pref = activeTabs.filter((t) => t.id == 'credit card')
            if (!pref.length) pref = activeTabs
            if (pref.length) current = pref[0]
            // current = pref.length ? pref[0] : undefined
        }
    }
    let initialTabSet = false
    $effect(() => {
        if (!initialTabSet && status == 'ready' && activeTabs.length && (!current || !activeTabs.includes(current))) {
            setInitialTab()
            initialTabSet = true
        }
    })

    function handleSuccess(ptype: string) {
        if (ptype == 'bill later') {
            userAllowedPaymentMethods.delete(ptype)
            setInitialTab()
        } else if (ptype == 'quotation') {
            Order.reset(Order.orderId)
        }
    }
</script>

<style>
    .nav-link {
        text-decoration: none;
    }
</style>

<div class="card">
    <div class="tabs">
        <ul class="nav nav-tabs text-bg-light">
            {#each tabs as tab}
                {#if allowedPaymentMethods.has(tab.id)}
                    <li class="nav-item">
                        <a
                            class="nav-link"
                            class:active={current && current.id == tab.id}
                            href="/"
                            onclick={(e) => {
                                e.preventDefault()
                                current = tab
                            }}>{$resolve(tab.text)}</a
                        >
                    </li>
                {/if}
            {/each}
        </ul>
        <div class="tab-content p-3">
            {#if status == 'loading' || loadingMethods || !current}
                <LoadingPattern pattern={['x', 'x']} />
            {:else}
                <current.component onSuccess={handleSuccess} />
            {/if}
        </div>
    </div>
</div>
